import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Eye, Calendar, User, Edit, FileText } from 'lucide-react';
import EditJobOfferModal from './EditJobOfferModal';
import BrouillonActionModal from './BrouillonActionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const ManagerOffresEnAttente = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobOffers, setJobOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBrouillonModal, setShowBrouillonModal] = useState(false);

  useEffect(() => {
    fetchJobOffers();
  }, []);

  const fetchJobOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offers = await response.json();
        setJobOffers(offers);
      } else {
        toast.error('Erreur lors du chargement des offres');
      }
    } catch (error) {
      console.error('Error fetching job offers:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToManager = () => {
    navigate('/manager');
  };

  const handleOfferClick = (offer) => {
    if (offer.status === 'a_modifier') {
      // Open edit modal for offers that need modification
      setSelectedOffer(offer);
      setShowEditModal(true);
    } else {
      // For "en_attente" offers, just show info (read-only)
      toast.info('Cette offre est en cours de validation par la RH');
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedOffer(null);
  };

  const handleOfferUpdated = () => {
    setShowEditModal(false);
    setSelectedOffer(null);
    fetchJobOffers(); // Refresh the list
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCreationDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Filter offers
  const offersEnAttente = jobOffers.filter(offer => offer.status === 'en_attente');
  const offersAModifier = jobOffers.filter(offer => offer.status === 'a_modifier');
  const offersBrouillon = jobOffers.filter(offer => offer.status === 'brouillon');

  const handleBrouillonOfferClick = (offer) => {
    setSelectedOffer(offer);
    setShowBrouillonModal(true);
  };

  const handleBrouillonAction = async (action) => {
    if (!selectedOffer) return;

    const token = localStorage.getItem('token');
    
    try {
      if (action === 'envoyer') {
        // Envoyer le brouillon à la RH (changer statut vers en_attente)
        const response = await fetch(`${API}/job-offers/${selectedOffer.id}/status?status=en_attente`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          toast.success('Offre envoyée à la RH pour validation');
          setShowBrouillonModal(false);
          setSelectedOffer(null);
          fetchJobOffers();
        } else {
          toast.error('Erreur lors de l\'envoi de l\'offre');
        }
      } else if (action === 'supprimer') {
        // Supprimer le brouillon
        const response = await fetch(`${API}/job-offers/${selectedOffer.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          toast.success('Brouillon supprimé avec succès');
          setShowBrouillonModal(false);
          setSelectedOffer(null);
          fetchJobOffers();
        } else {
          toast.error('Erreur lors de la suppression du brouillon');
        }
      } else if (action === 'updated') {
        // Refresh data after update
        fetchJobOffers();
      }
    } catch (error) {
      console.error('Error handling brouillon action:', error);
      toast.error('Erreur de connexion');
    }
  };

  // Company logos
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  return (
    <div className="manager-offres-attente-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToManager}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos[user?.company]}
            alt={`Logo ${user?.company}`}
            className="rh-header-logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <h1>Offres en attente - {user?.company}</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Tabs defaultValue="en_attente" className="recruitment-tabs">
            <TabsList className="recruitment-tabs-list">
              <TabsTrigger value="en_attente" className="recruitment-tab-trigger">
                En attente
                <span className="recruitment-tab-counter">{offersEnAttente.length}</span>
              </TabsTrigger>
              <TabsTrigger value="modification" className="recruitment-tab-trigger">
                Modification
                <span className="recruitment-tab-counter">{offersAModifier.length}</span>
              </TabsTrigger>
              <TabsTrigger value="brouillon" className="recruitment-tab-trigger">
                Brouillon
                <span className="recruitment-tab-counter">{offersBrouillon.length}</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab Content - En attente */}
            <TabsContent value="en_attente" className="recruitment-tab-content">
              <div className="recruitment-grid-compact">
                {offersEnAttente.map((offer) => (
                  <Card 
                    key={offer.id}
                    className="recruitment-card-compact cursor-pointer group"
                    onClick={() => handleOfferClick(offer)}
                  >
                    <CardContent className="recruitment-card-compact-content">
                      <div className="recruitment-card-compact-header">
                        <img
                          src={companyLogos[offer.company]}
                          alt={`Logo ${offer.company}`}
                          className="recruitment-card-compact-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Eye className="recruitment-card-compact-icon" />
                      </div>
                      <h3 className="recruitment-card-compact-title">
                        {offer.title}
                      </h3>
                      <div className="recruitment-card-compact-info">
                        <div className="recruitment-card-compact-info-item">
                          <span><strong>Société :</strong> {offer.company}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span>{formatCreationDate(offer.created_at)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {offersEnAttente.length === 0 && (
                  <div className="empty-state-compact">
                    <p>Aucune offre en attente de validation</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab Content - Modification */}
            <TabsContent value="modification" className="recruitment-tab-content">
              <div className="recruitment-grid-compact">
                {offersAModifier.map((offer) => (
                  <Card 
                    key={offer.id}
                    className="recruitment-card-compact recruitment-card-compact-modify cursor-pointer group"
                    onClick={() => handleOfferClick(offer)}
                  >
                    <CardContent className="recruitment-card-compact-content">
                      <div className="recruitment-card-compact-header">
                        <img
                          src={companyLogos[offer.company]}
                          alt={`Logo ${offer.company}`}
                          className="recruitment-card-compact-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Edit className="recruitment-card-compact-icon" style={{ color: '#f59e0b' }} />
                      </div>
                      <h3 className="recruitment-card-compact-title">
                        {offer.title}
                      </h3>
                      <div className="recruitment-card-compact-info">
                        <div className="recruitment-card-compact-info-item">
                          <span><strong>Société :</strong> {offer.company}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span>{formatCreationDate(offer.created_at)}</span>
                        </div>
                      </div>
                      {/* Affichage du message de justification de la RH */}
                      {offer.justification_modification && offer.justification_modification.trim() && (
                        <div className="modification-message">
                          <p className="text-red-700 text-sm">
                            <strong>Modification :</strong> {offer.justification_modification}
                          </p>
                        </div>
                      )}
                      <div className="modification-notice">
                        <p className="text-amber-600 text-sm font-medium">Renvoyée pour modification</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {offersAModifier.length === 0 && (
                  <div className="empty-state-compact">
                    <p>Aucune offre à modifier</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab Content - Brouillon */}
            <TabsContent value="brouillon" className="recruitment-tab-content">
              <div className="recruitment-grid-compact">
                {offersBrouillon.map((offer) => (
                  <Card 
                    key={offer.id}
                    className="recruitment-card-compact recruitment-card-compact-draft cursor-pointer group"
                    onClick={() => handleBrouillonOfferClick(offer)}
                  >
                    <CardContent className="recruitment-card-compact-content">
                      <div className="recruitment-card-compact-header">
                        <img
                          src={companyLogos[offer.company]}
                          alt={`Logo ${offer.company}`}
                          className="recruitment-card-compact-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="flex items-center">
                          <FileText className="text-gray-500 mr-2" size={16} />
                          <Edit className="recruitment-card-compact-icon" />
                        </div>
                      </div>
                      <h3 className="recruitment-card-compact-title">
                        {offer.title}
                      </h3>
                      <div className="recruitment-card-compact-info">
                        <div className="recruitment-card-compact-info-item">
                          <span><strong>Société :</strong> {offer.company}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span><strong>Créé le :</strong> {formatCreationDate(offer.created_at)}</span>
                        </div>
                        {offer.lieu_travail && (
                          <div className="recruitment-card-compact-info-item">
                            <span><strong>Lieu :</strong> {offer.lieu_travail}</span>
                          </div>
                        )}
                      </div>
                      <div className="draft-notice">
                        <p className="text-gray-600 text-sm font-medium">Brouillon enregistré</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {offersBrouillon.length === 0 && (
                  <div className="empty-state-compact">
                    <p>Aucun brouillon</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Edit Job Offer Modal */}
      {showEditModal && selectedOffer && (
        <EditJobOfferModal
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          onSuccess={handleOfferUpdated}
          offer={selectedOffer}
        />
      )}

      {/* Brouillon Action Modal */}
      {showBrouillonModal && selectedOffer && (
        <BrouillonActionModal
          isOpen={showBrouillonModal}
          onClose={() => setShowBrouillonModal(false)}
          onAction={handleBrouillonAction}
          offer={selectedOffer}
          userType="general"
        />
      )}
    </div>
  );
};

export default ManagerOffresEnAttente;